import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'

import { UsageChartCard } from '@/components/UsageChart'
import { Button } from '@/components/ui/button'
import { getOrgDetail } from '@/lib/admin/server'
import { getOrgUsageAdmin } from '@/lib/usage/server'
import { OrgDangerZone } from './-components/org-danger'
import {
  OrgLedgerCard,
  OrgMembersTable,
  OrgProxyUsersTable,
  OrgStatCards,
} from './-components/org-detail'
import {
  OrgApiRateLimitCard,
  OrgCredentialLimitCard,
  OrgGeneralCard,
} from './-components/org-general'
import { OrgPayments } from './-components/org-payments'
import { OrgPlanCard } from './-components/org-plan-card'

export const Route = createFileRoute(
  '/_authenticated/admin/organizations_/$orgId',
)({
  component: AdminOrgDetailPage,
})

function AdminOrgDetailPage() {
  const { orgId } = Route.useParams()
  const { data, isPending, error } = useQuery({
    queryKey: ['admin-org-detail', orgId],
    queryFn: () => getOrgDetail({ data: { organizationId: orgId } }),
  })
  const orgName = data?.name ?? 'this organization'

  return (
    <div className="flex flex-col gap-6">
      <Button asChild variant="ghost" size="sm" className="w-fit -ml-2">
        <Link to="/admin/organizations">
          <ArrowLeft data-icon="inline-start" />
          All organizations
        </Link>
      </Button>

      {error ? (
        <p className="text-sm text-destructive">{(error as Error).message}</p>
      ) : null}

      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-semibold tracking-tight">
          {isPending ? '…' : data?.name}
        </h2>
        {data ? (
          <p className="text-muted-foreground">
            {data.slug} · created{' '}
            {new Date(data.createdAt).toLocaleDateString()}
          </p>
        ) : null}
      </div>

      <OrgStatCards
        isPending={isPending}
        balanceBytes={data?.balanceBytes ?? 0}
        memberCount={data?.members.length ?? 0}
        proxyUserCount={data?.proxyUsers.length ?? 0}
      />

      {data ? <OrgGeneralCard orgId={orgId} currentName={data.name} /> : null}
      {data ? (
        <OrgCredentialLimitCard
          orgId={orgId}
          maxCredentials={data.maxCredentials}
          credentialCount={data.proxyUsers.length}
        />
      ) : null}

      <OrgPlanCard orgId={orgId} orgName={orgName} />
      <OrgPayments orgId={orgId} orgName={orgName} />

      <UsageChartCard
        title="Data usage"
        description="Bandwidth consumed across this organization's credentials."
        queryKey={(range) => ['admin-org-usage', orgId, range]}
        fetcher={(range) =>
          getOrgUsageAdmin({ data: { organizationId: orgId, range } })
        }
      />

      <OrgLedgerCard orgId={orgId} />

      <OrgMembersTable
        orgId={orgId}
        members={data?.members ?? []}
        pendingInvitationCount={data?.pendingInvitationCount ?? 0}
      />

      <OrgProxyUsersTable
        orgId={orgId}
        proxyUsers={data?.proxyUsers ?? []}
        isPending={isPending}
      />

      {data ? (
        <OrgApiRateLimitCard
          orgId={orgId}
          disabled={data.apiRateLimitDisabled}
        />
      ) : null}

      <OrgDangerZone orgId={orgId} orgName={orgName} />
    </div>
  )
}
